<template>
  <div class="relative">
    <Card v-if="!appointmentID">
      <CardBody>
        <div class="w-full flex gap-2">
          <div
            v-tippy="{
              content: isBookingDisabled ? 'Customer already has an upcoming appointment' : ''
            }"
          >
            <Button
              :label="$t('ui.actions.new')"
              icon="plus"
              :disabled="isBookingDisabled"
              @click="$emit('new-appointment')"
            />
          </div>
          <Button
            :disabled="
              selectedCallCenterCommsConfig?.emailAddress == null || agreementData.customer.contact.email == null
            "
            label="Send Invite"
            icon="paper-plane"
            severity="secondary"
            outlined
            @click="$emit('send-invite')"
          />
        </div>
        <div
          v-if="appointmentsData"
          class="mt-5"
        >
          <AgreementAppointments :appointments-list="appointmentsData" />
        </div>
        <div
          v-else
          class="mt-5"
        >
          <Skeleton
            height="1rem"
            width="15rem"
            class="mb-2"
          />
          <Skeleton
            height="4rem"
            class="mb-2"
          />
          <Skeleton
            height="4rem"
            class="mb-4"
          />
          <Skeleton
            height="1rem"
            width="15rem"
            class="mb-2"
          />
          <Skeleton
            height="4rem"
            class="mb-2"
          />
          <Skeleton
            height="4rem"
            class="mb-2"
          />
        </div>
      </CardBody>
    </Card>
    <div v-if="appointmentID">
      <AgreementAppointment
        :agreement-data="agreementData"
        @appointment-updated="$emit('appointment-updated', $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
// composables
import { computed } from 'vue'
import { useAbility } from '@casl/vue'
// stores
import { useCallCenterStore } from '@/stores/CallCenterStore'
// components
import Card from '@/components/card/Card.vue'
import CardBody from '@/components/card/CardBody.vue'
import Button from '@/components/button/Button.vue'
import Skeleton from '@/components/skeleton/Skeleton.vue'
import AgreementAppointments from '@/components/unique/agreement/AgreementAppointments.vue'
import AgreementAppointment from '@/components/unique/agreement/AgreementAppointment.vue'
// types
import type { Agreement, DealershipCommsConfigItem } from '@/types/domain/agreement'
import type { AppointmentListItem } from '@/types/domain/appointment'
// utilities
import dayjs from 'dayjs'

const { can } = useAbility()

const callCenterStore = useCallCenterStore()

interface Props {
  agreementData: Agreement
  appointmentsData: AppointmentListItem[] | null
  appointmentID: string | number | null
}

const props = defineProps<Props>()

defineEmits<{
  'new-appointment': []
  'send-invite': []
  'appointment-updated': [data: AppointmentListItem]
}>()

/**
 * Computed selected call center comms config based on selected call center
 */
const selectedCallCenterCommsConfig = computed<DealershipCommsConfigItem | null>(() => {
  if (!props.agreementData || !callCenterStore.selectedCallCenter) {
    return null
  }
  return (
    props.agreementData.dealership.commsConfig.find(
      config => config.callCenterId.toString() === callCenterStore.selectedCallCenter
    ) || null
  )
})

/**
 * Computed property to check if there are active upcoming appointments
 * Excludes cancelled appointments and appointments with outcomes
 */
const hasActiveUpcomingAppointments = computed(() => {
  if (!props.appointmentsData || !Array.isArray(props.appointmentsData)) {
    return false
  }

  return props.appointmentsData.some((appt: AppointmentListItem) => {
    // Check if appointment is in the future
    const isUpcoming = dayjs().isBefore(dayjs(appt.time))

    // Exclude cancelled appointments
    if (appt.isCancelled) {
      return false
    }

    // Exclude appointments with outcomes
    if (appt.outcome != null) {
      return false
    }

    return isUpcoming
  })
})

/**
 * Computed property to determine if booking should be disabled
 * Booking is disabled if user cannot book multiple upcoming appointments AND there are active upcoming appointments
 */
const isBookingDisabled = computed(() => {
  return !can('can_book_multiple_upcoming_appointments') && hasActiveUpcomingAppointments.value
})
</script>
